import { Grid, Typography } from '@material-ui/core'
import React, { useCallback, useMemo, useState } from 'react'
import styled, { css } from 'styled-components'

import { CartFragment } from '../../../graphql/magento'
import { readCartDispensaryPromoCodes } from '../../../lib/cart/dispensary-promo'
import { ToggleCodesAction } from '../../../lib/cart/reducers/cart-state'
import {
  cartSubtotal,
  coloradoDeliveryFee,
  discountAmount,
  formatAsCurrencyOrFree,
  formatAsCurrencyOrZero,
  rebateAmount,
  salesTax,
  taxType,
} from '../../../utils/priceTools'
import CartPromoCode from '../../cart/CartPromoCode'
import StyledCartSummaryRow from '../../cart/CartSummaryRow'
import PromoTotalSummarySection from './PromoTotalSummarySection'
import TotalSummarySection from './TotalSummarySection'

const StyledSubtitle = styled(Typography)`
  && {
    font-weight: bold;
    padding-left: 15px;
    padding-right: 15px;
    margin: 10px 0;
  }
`

const StyledTotalRow = styled(Grid)`
  && {
    display: flex;
    font-weight: bold;
    padding: 0 15px 10px;
    @media (min-width: 600px) {
      border-top: 1px solid ${({ theme }) => theme.colors.component.checkoutBorderLight};
      margin-top: 10px;
      padding: 15px;
    }
  }
`

const StyledTotalSummarySection = styled(TotalSummarySection)`
  padding-left: 15px;
  padding-right: 15px;
`

export interface TotalSummaryProps {
  cart: CartFragment
  className?: string
  roundBottomCorners?: boolean
}

const TotalSummary: React.FC<TotalSummaryProps> = ({ cart, className }) => {
  const {
    applied_coupons: appliedCoupons,
    dispensary_promo_code: dispensaryPromoCode,
    prices,
    shipping_addresses: [shippingAddress],
  } = cart
  const grandTotal = prices?.grand_total?.value
  const rebateAmountText = rebateAmount(cart)
  const coloradoDeliveryFeeText = coloradoDeliveryFee(cart)
  const salesTaxText = salesTax({ prices })
  const shippingMethod = shippingAddress?.selected_shipping_method
  const shippingAmount = shippingMethod?.amount?.value
  const subtotalText = cartSubtotal(cart)

  const [codesCollapsed, setCodesCollapsed] = useState(true)

  // simulate dispatch from cart state reducer
  const dispatch: React.Dispatch<ToggleCodesAction> = useCallback(({ type }) => {
    if (type === 'toggleCodes') {
      setCodesCollapsed((prevState) => !prevState)
    }
  }, [])

  const cartDispensaryPromoCodes = useMemo(
    () => (dispensaryPromoCode ? readCartDispensaryPromoCodes(dispensaryPromoCode) : undefined),
    [dispensaryPromoCode],
  )

  return (
    <Grid className={className} container item xs={12}>
      <Grid item xs={12}>
        <CartPromoCode
          cart={cart}
          codesCollapsed={codesCollapsed}
          dispatch={dispatch}
          formDisabled={false}
        />
      </Grid>
      <Grid item xs={12}>
        <StyledSubtitle variant="subtitle2">SUMMARY</StyledSubtitle>
      </Grid>
      <StyledTotalSummarySection name="Your Subtotal:" amount={subtotalText} />
      <StyledTotalSummarySection
        name="Shipping:"
        amount={formatAsCurrencyOrFree({ amount: shippingAmount || 0 })}
      />
      <StyledTotalSummarySection name={`Sales Tax${taxType({ prices })}:`} amount={salesTaxText} />
      {coloradoDeliveryFeeText && (
        <StyledTotalSummarySection
          name={'Colorado Retail Delivery Fee:'}
          amount={coloradoDeliveryFeeText}
        />
      )}
      <PromoTotalSummarySection
        amount={discountAmount({ appliedCoupons, prices })}
        cartDispensaryPromoCodes={cartDispensaryPromoCodes}
        coupons={appliedCoupons}
      />
      {rebateAmountText && (
        <StyledTotalSummarySection name="Applied Rewards:" amount={rebateAmountText} />
      )}
      <StyledTotalRow container item xs={12} justifyContent="space-between">
        <Grid item>
          <Typography variant="subtitle2">Total:</Typography>
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">
            {formatAsCurrencyOrZero({ amount: grandTotal || 0 })}
          </Typography>
        </Grid>
      </StyledTotalRow>
    </Grid>
  )
}

const StyledTotalSummary = styled(TotalSummary)`
  border-top: 1px solid ${({ theme }) => theme.colors.component.checkoutBorderLight};
  border-bottom: 1px solid ${({ theme }) => theme.colors.component.checkoutBorderLight};
  background-color: ${({ theme }) => theme.colors.coolGray.cool150};
  padding-top: 15px;

  // for CartPromoCode
  ${StyledCartSummaryRow} {
    padding-left: 15px;
    padding-right: 15px;
  }

  @media (min-width: 600px) {
    border-left: 1px solid ${({ theme }) => theme.colors.component.checkoutBorderLight};
    border-right: 1px solid ${({ theme }) => theme.colors.component.checkoutBorderLight};
    ${({ roundBottomCorners }) =>
      roundBottomCorners &&
      css`
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
      `};
  }
`

export default StyledTotalSummary
